import { useEffect } from 'react';

import { useQueryClient } from 'react-query';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useStompClient } from 'react-stomp-hooks';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import useAnalytics from 'hooks/useAnalytics';
import useUserHook from 'hooks/useUserHook';

import { useProjectTreeStore } from 'store/projectContract.store';

import { getProgramGeneralHierarchyList } from 'api/endPoints/getProgramGeneralHierarchyList';
import { useQueryGetComponentsConfiguration } from 'api/queries/getComponentsConfiguration.query';
import { useQueryGetNotificationsConnectionStatus } from 'api/queries/getNotificationsConnectionStatus.query';

import { PageNotFound } from 'components/common/ErrorPages/pageNotFound';
import { PageNotFoundMobile } from 'components/common/ErrorPages/pageNotFound.mobile';
import { LoaderSpinner } from 'components/common/LodaerSpinner/LoaderSpinner';
import {
    ProjectTreeNode,
    firstEnterUpdateProjectTreeStore,
} from 'components/common/TreeProgramFilter/ProjectTree/projectTree.utils';

import Header from 'components/App/AuthenticateLayout/Header/header';
import { isUserOnMobile } from 'components/App/AuthenticateLayout/authenticate.utils';
import classes from 'components/App/AuthenticateLayout/authenticateLayout.module.scss';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import Dashboards from 'components/Dashboards/dashboards';
import { MobileRoutes } from 'components/Mobile/mobile';
import Settings from 'components/Settings/settings';

const defaultLocation = AppRoutes.buildPath(AppRoutes.DASHBOARD_EXECUTION_PATH, executionConfig.statusSummary.link);

const AuthenticateLayout = () => {
    const { homePageUrl, isSwitchUser } = useUserHook();
    const defaultPath = homePageUrl && homePageUrl.length > 0 ? homePageUrl : defaultLocation;
    const queryClient = useQueryClient();
    const { data: notificationsConnectionStatus } = useQueryGetNotificationsConnectionStatus();
    const { isLoading: isLoadingConfigurations } = useQueryGetComponentsConfiguration();

    /**
     * stompClient instance to communicate with StompSessionProvider
     * https://stomp-js.github.io/api-docs/latest/classes/Client.html
     */
    const stompClient = useStompClient();

    /**
     * if notificationsConnectionStatus is false then deactivate the socket connection
     */
    useEffect(() => {
        if (notificationsConnectionStatus === false && stompClient) {
            stompClient.deactivate().then(() => {
                console.log(`stompClient deactivated`);
            });
        }
    }, [notificationsConnectionStatus]);

    useAnalytics();

    // set default project and contract

    useEffect(() => {
        queryClient.prefetchQuery(['getGeneralHierarchyList'], getProgramGeneralHierarchyList).then(() => {
            const project = useProjectTreeStore.getState().project;
            const contract = useProjectTreeStore.getState().contract;
            const update = useProjectTreeStore.getState().update;
            const data = queryClient.getQueryData('getGeneralHierarchyList') as ProjectTreeNode | null;
            firstEnterUpdateProjectTreeStore({ project, contract, update, data });
        });
    }, [isSwitchUser]);

    if (isLoadingConfigurations) {
        return (
            <div className={classes.configurationLoaderWrapper}>
                <LoaderSpinner className={classes.configurationLoader} />
            </div>
        );
    }

    const isMobile = isUserOnMobile();
    if (isMobile) {
        return (
            <Switch>
                <Route path={`/${AppRoutes.MOBILE}`} component={MobileRoutes} />
                <Redirect exact from={'/'} to={`/${AppRoutes.MOBILE}`} />
                <Route path={'*'} component={PageNotFoundMobile} />
            </Switch>
        );
    }

    return (
        <>
            <Header />
            <Switch>
                <Route path={`/${AppRoutes.DASHBOARD}`} component={Dashboards} />
                <Route path={`/${AppRoutes.SETTINGS}`} component={Settings} />
                <Redirect exact from={'/'} to={defaultPath} />
                <Route path={'*'} component={PageNotFound} />
            </Switch>
        </>
    );
};

export default AuthenticateLayout;
